import React from 'react';
import { CheckboxCheckedIcon, EditIcon3, DeleteIcon } from 'assets';
import { JobCriteriaData } from '../types';
import { WorkMotivatorsEditModal, DeleteSectionModal } from 'components';
import { useToast, useWorkCultureTraitsEdit, useWorkCultureTraitsDelete } from 'hooks';
import { WorkCultureTraitsPayload } from 'hooks';
import { toTitleCase } from 'utils/titleCase';

interface WorkMotivatorsSectionProps {
  assessmentId: string | null;
  data: JobCriteriaData['workMotivators'];
  workplaceQualities: JobCriteriaData['workplaceQualities'];
  onEdit: () => void;
  onDelete: () => void;
  onSave: (updatedData: JobCriteriaData['workMotivators']) => void;
  disabled?: boolean;
}

const WorkMotivatorsSection: React.FC<WorkMotivatorsSectionProps> = ({
  assessmentId,
  data,
  workplaceQualities,
  onEdit,
  onDelete,
  onSave,
  disabled,
}) => {
  const { openToast } = useToast();
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const {
    submitWorkCultureTraits,
    isSubmitting,
  } = useWorkCultureTraitsEdit(assessmentId);
  const {
    deleteWorkCultureTraits,
  } = useWorkCultureTraitsDelete(assessmentId);

  const onSubmit = (formData: JobCriteriaData['workMotivators']) => {
    if (!assessmentId || isSubmitting) {
      openToast({
        show: true,
        heading: 'Required',
        text: 'Assessment ID is required',
        type: 'error',
        timeOut: 5000,
      });
      return;
    }

    const payload: WorkCultureTraitsPayload = {
      assessment_id: assessmentId,
      quality1: workplaceQualities.workplaceQualities[0]?.title || '',
      quality2: workplaceQualities.workplaceQualities[1]?.title || '',
      quality3: workplaceQualities.workplaceQualities[2]?.title || '',
      workCultureTraits: formData.workMotivators.reduce((acc, trait, index) => ({
        ...acc,
        [`culture${index + 1}`]: trait.title,
        [`culture_expectation${index + 1}`]: trait.description,
        [`check${index + 1}`]: trait.checked,
      }), {}),
    };

    submitWorkCultureTraits(payload, {
      onSuccess: () => {
        onSave(formData);
        setIsEditModalOpen(false);
      },
      onError: () => {
        setIsEditModalOpen(false);
      },
    });
  };

  const handleDeleteConfirm = () => {
    if (!assessmentId) {
      openToast({
        show: true,
        heading: 'Required',
        text: 'Assessment ID is required for deletion',
        type: 'info',
        timeOut: 5000,
      });
      return;
    }

    deleteWorkCultureTraits({ assessment_id: assessmentId, section: 'work_motivators' }, {
      onSuccess: () => {
        onDelete();
        setIsDeleteModalOpen(false);
      },
    });
  };

  return (
    <section className="border-t border-[#dfe1e6] pt-6 mb-8 relative">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl sm:text-2xl font-medium sm:font-semibold m-0">Work Motivators</h2>
        {!disabled && (
          <div className="flex gap-3.5 items-center">
            <span className="cursor-pointer">
              <EditIcon3 onClick={() => { onEdit(); setIsEditModalOpen(true); }} />
            </span>
            <div className="relative group cursor-pointer">
              <DeleteIcon onClick={() => setIsDeleteModalOpen(true)} />
              <div className="absolute bottom-full right-0 mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-52 z-10">
                <span className="block bg-[#1d2939] text-white text-xs rounded-lg px-3.5 py-2.5 leading-relaxed">
                  You can delete section you don't want to screen at this stage
                </span>
                <span className="absolute top-full right-[18px] w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-[#1d2939]" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        {data.workMotivators.map((motivator, index) => (
          motivator.checked && (
            <div key={index} className="flex gap-[17px] items-start">
              <div className="mt-1">
                <div className="w-7 h-7 rounded flex items-center justify-center">
                  <CheckboxCheckedIcon />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-base font-semibold text-[#172b4d] m-0 mb-1">{toTitleCase(motivator.title)}</p>
                <p className="text-sm font-normal leading-5 text-[#1D2939]">{motivator.description}</p>
              </div>
            </div>
          )
        ))}
      </div>

      <WorkMotivatorsEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        currentData={data}
        onSave={onSubmit}
        isSubmitting={isSubmitting}
      />

      <DeleteSectionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        sectionName="Work Motivators"
      />
    </section>
  );
};

export default WorkMotivatorsSection;